/**
 * MultipleChoiceV2 - MCQ answer selection component
 *
 * Features:
 * - Modern answer option styling
 * - Cross-out functionality for elimination
 * - Keyboard navigation (1-4 to select, Ctrl+1-4 to cross out)
 * - Smooth animations
 */

import { memo, useMemo, useCallback } from 'react'
import { useChallengeV2 as useChallengeStoreV2 } from '../../../hooks/useChallenge'
import AnswerOptionV2 from './AnswerOptionV2'

const MultipleChoiceV2 = memo(({ question }) => {
  // Store state
  const selectedAnswer = useChallengeStoreV2((state) => state.selectedAnswer)
  const crossedOutAnswers = useChallengeStoreV2((state) => state.crossedOutAnswers)

  // Store actions
  const setSelectedAnswer = useChallengeStoreV2((state) => state.setSelectedAnswer)
  const toggleCrossOut = useChallengeStoreV2((state) => state.toggleCrossOut)

  // Get answer options
  const answerOptions = question?.answerOptions || []

  // Check if option is crossed out
  const isAnswerCrossedOut = useCallback(
    (option) => {
      return crossedOutAnswers.has(option)
    },
    [crossedOutAnswers]
  )

  // Handle answer selection
  const handleSelect = useCallback(
    (option) => {
      if (!isAnswerCrossedOut(option)) {
        setSelectedAnswer(option)
      }
    },
    [setSelectedAnswer, isAnswerCrossedOut]
  )

  // Handle cross out
  const handleCrossOut = useCallback(
    (option) => {
      toggleCrossOut(option)
    },
    [toggleCrossOut]
  )

  // Memoize option components
  const optionComponents = useMemo(() => {
    return answerOptions.map((option, index) => {
      const optionLabel = String.fromCharCode(65 + index) // A, B, C, D
      const isSelected = selectedAnswer === optionLabel
      const isCrossedOut = isAnswerCrossedOut(optionLabel)

      return (
        <AnswerOptionV2
          key={option.id || index}
          option={option}
          label={optionLabel}
          isSelected={isSelected}
          isCrossedOut={isCrossedOut}
          onSelect={handleSelect}
          onCrossOut={handleCrossOut}
          contentType={question?.contentType || question?.content_type}
        />
      )
    })
  }, [
    answerOptions,
    selectedAnswer,
    isAnswerCrossedOut,
    handleSelect,
    handleCrossOut,
    question?.contentType,
    question?.content_type
  ])

  if (!answerOptions.length) {
    return (
      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
        No answer options available
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          Answer Choices
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Press 1-4 to select, Ctrl+1-4 to cross out
        </span>
      </div>

      <div className="space-y-2 sm:space-y-3">{optionComponents}</div>
    </div>
  )
})

MultipleChoiceV2.displayName = 'MultipleChoiceV2'

export default MultipleChoiceV2
